import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import BoundaryFormFields from 'components/forms/BoundaryFormFields';

import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props, UpdateBoundaryFormSchema } from './types';

import styles from './styles.module.scss';

const UpdateBoundaryForm: FunctionComponent<Props> = ({
    className = '',
    formValues,
    isClosing,
    onClose,
    setIsClosing,
    testId = TEST_ID
}) => {
    const { updateBoundary } = useBoundary();
    const { assignFieldToBoundary, fetchFieldHierarchyFormItems } = useField();
    const { SAVE } = useTranslation();

    const getFieldData = async (): Promise<UpdateBoundaryFormSchema> => {
        let parsedFormData: Partial<UpdateBoundaryFormSchema> =
            formValues as unknown as UpdateBoundaryFormSchema;

        if (formValues?.siteId) {
            const fieldData = await fetchFieldHierarchyFormItems({ id: formValues.siteId });

            parsedFormData = {
                ...parsedFormData,
                ...fieldData
            };
        }

        return parsedFormData as unknown as UpdateBoundaryFormSchema;
    };

    const form = useForm<UpdateBoundaryFormSchema>({
        defaultValues: getFieldData,
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async (data: UpdateBoundaryFormSchema) => {
        const { boundaryParts, field, id, name } = data;

        if (field?.value) {
            await assignFieldToBoundary({
                boundaryId: id,
                siteId: field?.value ?? ''
            });
        }

        await updateBoundary({ boundary: { boundaryParts, name }, id });
        onClose?.();
    };

    return (
        <VerticalContainer className={`${styles.updateBoundaryForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <BoundaryFormFields />

                    <FormSubmitPanel
                        buttonText={SAVE}
                        className={styles.formSubmitPanel}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default UpdateBoundaryForm;
